import isNumber from 'lodash/isNumber';
import uniqBy from 'lodash/uniqBy';
import { combineLatest, Observable, of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { formatDayFromUtc, formatToday } from '../../../utils/date';
import { roundValue } from '../../../utils/math';
import { queryHashGenerator } from '../../../utils/queryHash';
import { scaleZScore, zScore } from '../../../utils/scoring';
import { testMetaString } from '../../../utils/testMetaString';
import {
  EntityType,
  EZScorePreference,
  IAggregateWithMetric,
  IComputedAggregate,
  ISelectableEntity,
  ITag,
  ITestType,
} from '../../types';
import {
  crossTestAggregates,
  ICrossTestAggregates,
} from '../crossTestAggregatesProvider';
import { IComparisonPopulationSetting } from '../settings/comparisonPopulationSettingsProvider';
import {
  IProfileReportSettings,
  IProfileReportSettingsState,
  profileReportSettingsProvider,
} from '../settings/reports/profileReportSettingsProvider';
import { determinePopulation } from '../utils/determinePopulation';
import {
  convertTimeframeToLocalTime,
  formatTimeframe,
} from '../utils/formatTimeframe';
import { gatherQueryHashes } from '../utils/gatherQueryHashes';
import { isValidDateRange } from '../utils/isValidDateRange';
import { IReportState } from './types';

export interface IZScore {
  score: number;
  targetMean: number;
  populationMean: number;
  difference: number;
  differencePercentage: number;
  populationSd: number;
  units: string;
  testMeta: string;
  metricLabel: string;
  targetCount: number;
  populationCount: number;
  roundingPrecision: number;
  date?: string;
  scaled: boolean;
}

export interface ISessionScores {
  [metricLabel: string]: IZScore;
}
export interface IZScoreResult {
  timeframe: string;
  timestamp: number;
  scores: ISessionScores;
  populationTimeframe: string;
}

export interface IZScoreDetails {
  metricLabel: string;
  testMeta: string;
  details: IZScore[];
}

export interface IZScoreReportState
  extends IReportState,
    IProfileReportSettingsState {
  data: IZScoreResult[];
  details: IZScoreDetails[];
  scaled: boolean;
}

/**
 * @description pipeline that observes the profile data requested based on the profile report settings
 * 	and then determines and emits the state of the report
 * @param entityType
 * @param entityId
 * @returns
 */
export const zscoreReportProvider = (
  entityType: EntityType,
  entityId: string
): Observable<IZScoreReportState> => {
  return profileReportSettingsProvider().pipe(
    /// observe the target aggregates as defined in the settings
    switchMap(
      (
        settings: IProfileReportSettingsState
      ): Observable<[IProfileReportSettingsState, ICrossTestAggregates]> => {
        const profileSettingsValidation = validateSettings(settings);
        if (profileSettingsValidation.isValid) {
          /// gather the list of unique query hashes
          const queryHashes = gatherQueryHashes(
            settings?.selected?.settings?.metrics
          );
          return combineLatest([
            of<IProfileReportSettingsState>(settings),
            crossTestAggregates(
              queryHashes,
              entityId,
              entityType,
              settings.targetTimeframe?.dateType,
              settings.targetTimeframe?.dateRange
            ),
          ]);
        } else {
          return of([settings, null]);
        }
      }
    ),
    switchMap(
      (
        profileResults: [IProfileReportSettingsState, ICrossTestAggregates]
      ): Observable<
        [
          IProfileReportSettingsState,
          ICrossTestAggregates,
          ICrossTestAggregates
        ]
      > => {
        const [settings, profileAggregates] = profileResults;

        /// gather the list of unique query hashes
        const queryHashes = gatherQueryHashes(
          settings?.selected?.settings?.metrics
        );
        const [populationEntityType, populationEntityId] = determinePopulation(
          settings.comparisonPopulation.entity,
          {
            entityId,
            entityType,
          }
        );
        const afterData =
          settings.targetTimeframe?.dateType?.value === 'all-time'
            ? null
            : profileAggregates;

        return combineLatest([
          of<IProfileReportSettingsState>(settings),
          of<ICrossTestAggregates>(profileAggregates),
          crossTestAggregates(
            queryHashes,
            populationEntityId,
            populationEntityType as EntityType,
            settings.comparisonPopulation.dateRangeType,
            settings.comparisonPopulation.dateRangeValues,
            afterData
          ),
        ]);
      }
    ),
    /// map the aggregates to a state
    map(
      (
        data: [
          IProfileReportSettingsState,
          ICrossTestAggregates,
          ICrossTestAggregates
        ]
      ): IZScoreReportState => {
        const results = makeResults(
          data[0]?.selected?.settings,
          data[1],
          data[0]?.comparisonPopulation,
          data[2]
        );
        const details = makeDetails(results);

        /// run validations
        let finalValidation: IValidationResult;
        /// check settings
        const settingsValidation = validateSettings(data[0]);
        if (!settingsValidation.isValid) {
          finalValidation = settingsValidation;
        } else {
          finalValidation = validateData(data[1], details);
        }
        const localComparisonTimeframe = convertTimeframeToLocalTime({
          dateType: data[0]?.comparisonPopulation?.dateRangeType,
          dateRange: data[0]?.comparisonPopulation?.dateRangeValues,
        });
        return {
          ...finalValidation,
          ...data[0],
          targetTimeframe: convertTimeframeToLocalTime(
            data[0]?.targetTimeframe
          ),
          comparisonPopulation: {
            entity: data[0]?.comparisonPopulation?.entity,
            dateRangeValues: localComparisonTimeframe.dateRange,
            dateRangeType: localComparisonTimeframe.dateType,
          },
          scaled:
            data[0]?.comparisonPopulation?.zScorePreference ===
            EZScorePreference.Scaled,
          data: results,
          timeframe: formatToday(),
          details,
          settings: data[0]?.selected,
        };
      }
    )
  );
};
/**
 * @description creates the details from results
 * @param results
 * @returns
 */
const makeDetails = (results: IZScoreResult[]): IZScoreDetails[] => {
  const details: { [key: string]: IZScore[] } = {};

  for (const result of results) {
    Object.keys(result.scores).forEach((key: string): void => {
      if (details[key]) {
        details[key].push({
          date: formatDayFromUtc(result.timestamp),
          ...result.scores[key],
        });
      } else {
        details[key] = [
          {
            date: formatDayFromUtc(result.timestamp),
            ...result.scores[key],
          },
        ];
      }
    });
  }

  return Object.keys(details).map((key: string): IZScoreDetails => {
    const { testMeta, metricLabel } = details[key][0];
    return {
      testMeta,
      metricLabel,
      details: details[key].filter((val: IZScore): boolean =>
        isNumber(val.score)
      ),
    };
  });
};
/**
 * @description generates the individual profile result for each metric defined in the report settings
 * @param settings
 * @param aggregates
 * @returns
 */
const makeResults = (
  targetSettings: IProfileReportSettings,
  targetAggregates: ICrossTestAggregates,
  populationSettings: IComparisonPopulationSetting,
  populationAggregates: ICrossTestAggregates
): IZScoreResult[] => {
  /// gather the uniq session ids
  const sessionIds = uniqBy(
    Object.keys(targetAggregates ?? []).reduce(
      (acc: number[], curr: string): number[] => {
        return acc.concat(
          targetAggregates[curr].sessions.map(
            (agg: IComputedAggregate): number => +agg.meta.id
          )
        );
      },
      []
    ),
    (id: number): number => id
  );

  const scaled =
    populationSettings?.zScorePreference === EZScorePreference.Scaled;

  /// create result for each session
  return sessionIds.map((sessionId: number): IZScoreResult => {
    const scores: ISessionScores = {};
    let populationTimeframe: string = null;
    /// calculate for each selected metric
    for (const metricSelection of targetSettings.metrics) {
      const testMeta = testMetaString(
        metricSelection.testType?.label,
        metricSelection?.tags?.map(
          (se: ISelectableEntity): ITag => se.entity as ITag
        ) ?? []
      );
      const queryHash = queryHashGenerator(
        metricSelection.testType.value,
        metricSelection.tags.map((se: ISelectableEntity): string => se.value)
      );
      const targetData = targetAggregates[queryHash].sessions?.find(
        (agg: IComputedAggregate): boolean =>
          agg.meta.id === sessionId.toString()
      )?.data[metricSelection.metric?.value] as IAggregateWithMetric;

      const populationData =
        populationAggregates?.[queryHash]?.combined?.data?.[
          metricSelection?.metric?.value
        ];

      const meanA = targetData?.mean;
      const meanB = populationData?.mean;

      if (targetData?.count > 0 && populationData?.count > 2) {
        const total = meanA + meanB;
        const change = meanA - meanB;
        const zscore = zScore(
          meanA,
          meanB,
          populationData?.populationSd ?? 0,
          targetData.direction
        );

        populationTimeframe = formatTimeframe(
          populationAggregates?.[queryHash]?.start,
          populationAggregates?.[queryHash]?.end
        );

        scores[
          `${(metricSelection.testType.entity as ITestType).abbr}:${
            metricSelection.metric.label
          }`
        ] = {
          score: scaled
            ? roundValue(scaleZScore(zscore), 0)
            : roundValue(zscore, 2),
          targetMean: meanA,
          targetCount: targetData?.count,
          populationCount: populationData?.count,
          populationMean: meanB,
          difference: change,
          differencePercentage: (change / total) * 100,
          populationSd: populationData?.populationSd,
          metricLabel: metricSelection.metric.label,
          testMeta,
          units: targetData?.units,
          roundingPrecision: metricSelection.metric.entity.roundingPrecision,
          scaled,
        };
      } else {
        scores[
          `${(metricSelection.testType.entity as ITestType).abbr}:${
            metricSelection.metric.label
          }`
        ] = {
          score: null,
          targetMean: meanA ?? null,
          targetCount: targetData?.count ?? 0,
          populationCount: populationData?.count ?? 0,
          populationMean: meanB ?? null,
          difference: null,
          differencePercentage: null,
          populationSd: populationData?.populationSd,
          metricLabel: metricSelection.metric.label,
          testMeta,
          units: targetData?.units,
          roundingPrecision: metricSelection.metric.entity.roundingPrecision,
          scaled,
        };
      }
    }
    return {
      timeframe: formatDayFromUtc(sessionId),
      timestamp: sessionId,
      scores,
      populationTimeframe,
    };
  });
};

interface IValidationResult {
  isValid: boolean;
  errorCode?: string;
}

/**
 * @description validates the user settings for the report
 * @param settings
 * @returns
 */
const validateSettings = (
  settings: IProfileReportSettingsState
): IValidationResult => {
  /// check to make sure a template is selected
  if (!settings?.selected) {
    return {
      isValid: false,
      errorCode: 'missing-template',
    };
  }
  /// check to make sure metrics are defined
  if (!(settings?.selected?.settings?.metrics?.length > 0)) {
    return {
      isValid: false,
      errorCode: 'missing-metrics',
    };
  }
  /// check to make sure data set A is valid
  if (!validTarget(settings)) {
    return {
      isValid: false,
      errorCode: 'invalid-target',
    };
  }

  return { isValid: true };
};

/**
 * @description validates that there is data for both the target and the population
 * @param target
 * @param population
 * @returns
 */
const validateData = (
  target: ICrossTestAggregates,
  details: IZScoreDetails[]
): IValidationResult => {
  /// check to see if the target has data
  const targetDataExists = Object.keys(target).reduce(
    (acc: boolean, curr: string): boolean => {
      if (acc) return acc;
      return target[curr].sessions.length > 0;
    },
    false
  );

  if (!targetDataExists) {
    return {
      isValid: false,
      errorCode: 'target-insufficient-data',
    };
  }

  /// check to see if the population has data
  const populationDataExists = details.reduce(
    (hasData: boolean, item: IZScoreDetails): boolean => {
      if (hasData) return hasData;
      return item.details.length > 0;
    },
    false
  );

  if (!populationDataExists) {
    return {
      isValid: false,
      errorCode: 'population-insufficient-data',
    };
  }

  return {
    isValid: true,
    errorCode: null,
  };
};

/**
 * @description determines if the settings for the profile target are valid
 * @param settings
 * @returns
 */
const validTarget = (settings: IProfileReportSettingsState): boolean => {
  if (
    !isValidDateRange({
      dateType: settings?.targetTimeframe?.dateType,
      dateRange: settings?.targetTimeframe?.dateRange,
    })
  ) {
    return false;
  }
  return true;
};
